import React, { useContext, useEffect, useState } from 'react'
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import MultiSelectEntity from '../RefineSearch/MultiSelectEntity';
import Popup from '../Common/Popup';
import Constants from '../../Constants';
import DefinitionModal from '../DefinitionModal';
import { Tooltip } from 'antd';
import { FaInfoCircle } from 'react-icons/fa';

/**
 * The `DealTypeFilter` function is a component in JavaScript that renders a filter for selecting deal
 * types, either as a single select or a multi-select option.
 * @returns a JSX element.
 */
function DealTypeFilter({
    DealTypeOption,
    setDealTypeOption,
    customPathname,
    dataPath,
    isMultiSelect = false,
    showAll = true,
    label = "Deal Type",
    isPopupOpen,
    setIsPopupOpen,
    menu
}) {

    const { dealTypeFilterResponse, refineSearchFilters } = useContext(PeFilterContext);
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    useEffect(() => {
        const industryIds = refineSearchFilters[customPathname]?.dealType?.map(value => value.id) ?? [];
        setDealTypeOption(dealTypeFilterResponse[dataPath]?.map(industry => ({ ...industry, isChecked: industryIds.includes(industry.id) })))
    }, [dealTypeFilterResponse, customPathname, refineSearchFilters, setDealTypeOption, dataPath])


  /**
   * The function `handleDealTypeChange` is used to handle changes in the deal type selection, either
   * for a single select or a multi-select option.
   * @param e - The parameter `e` is an event object that is passed to the `handleDealTypeChange`
   * function. It represents the event that triggered the function, such as a change event on an input
   * element.
   */
    const handleDealTypeChange = (e) => {
        if (isMultiSelect) {
            const { name, checked, id } = e.target;
            let currentDeals = [];
            if (name === "allSelect") {
                currentDeals = DealTypeOption.map((industry) =>
                    ({ ...industry, isChecked: checked })
                );
                setDealTypeOption(currentDeals);
            } else {
                currentDeals = DealTypeOption.map((industry) =>
                    industry.id === parseInt(id) ? { ...industry, isChecked: checked } : industry
                );
                setDealTypeOption(currentDeals);
            }
        } else {
            const value = e.target.value;
            setDealTypeOption(DealTypeOption?.map(type => ({ ...type, isChecked: value === "" ? false : type.id === parseInt(value) })));
        }
    };

    const selected = DealTypeOption?.filter(option => option.isChecked)?.length ?? 0
    const selectedOption = DealTypeOption?.filter(option => option.isChecked)?.at(0)

    const getDisplayText = () => {
        if (isMultiSelect) {
            return selected > 0 ? `${selected} selected` : "Select types";
        } else {
            return selectedOption?.name || "Select type";
        }
    };

    const definitionInfo = dataPath === "ma"
        ? Constants.MAdealType
        : null;

    const [showDefModal, setShowDefModal] = useState(false);
    
    const openModal = (e) => {
        e.stopPropagation(); // Prevent button click from toggling popup
        setShowDefModal(true);
    }
    
    const closeModal = () => {
        setShowDefModal(false);
    }

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100  text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2 flex items-center gap-2">{label} 
                    {dataPath === "ma" && <Tooltip title='Definitions'>
                        <FaInfoCircle 
                            size={12} 
                            color="grey" 
                            className="cursor-pointer" 
                            onClick={openModal}
                        />
                    </Tooltip>}
                <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. {DealTypeOption.slice(0,2).map(item => item.name).join(', ')}</span></span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {((isMultiSelect && selected > 0) || (!isMultiSelect && selectedOption)) && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {getDisplayText()}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup onClose={() => setPopupOpen(false)} definitionInfo={definitionInfo} label={label}>
                    <div className="p-4">
                        {isMultiSelect ? (
                            <MultiSelectEntity
                                handleEntityChange={handleDealTypeChange}
                                primaryEntity={DealTypeOption}
                                showAll={showAll}
                                searchBox={false}
                            />
                        ) : (
                            <div className="space-y-2">
                                <div className="max-h-60 overflow-y-auto space-y-2 grid grid-cols-3">
                                    {showAll && (
                                        <label className="flex items-center space-x-2 pt-1 hover:bg-gray-50 rounded cursor-pointer">
                                            <input
                                                type="radio"
                                                name="dealType"
                                                value=""
                                                checked={!selectedOption}
                                                onChange={handleDealTypeChange}
                                                className="text-[#D9BD7F] focus:ring-[#D9BD7F]"
                                            />
                                            <span className=" text-[13px] text-black font-semibold">Both</span>
                                        </label>
                                    )}
                                    {/* <div className=''> */}
                                    {DealTypeOption && DealTypeOption.length > 0 ? (
                                        DealTypeOption.map((dealType) => (
                                            <label key={dealType.id} className="flex items-center space-x-2 text-black font-semibold hover:bg-gray-50 rounded cursor-pointer py-2">
                                                <input
                                                    type="radio"
                                                    name="dealType"
                                                    value={dealType.id}
                                                    checked={selectedOption?.id === dealType.id}
                                                    onChange={handleDealTypeChange}
                                                    className="text-[#D9BD7F] focus:ring-[#D9BD7F]"
                                                />
                                                <span className=" text-[13px]">{dealType.name}</span>
                                            </label>
                                        ))
                                    ) : (
                                        <div className="text-sm text-gray-500 p-2">No results found!</div>
                                    )}
                                {/* </div> */}
                                </div>
                            </div>
                        )}
                    </div>
                </Popup>
            )}
            {showDefModal && <DefinitionModal label={label} definitionInfo={definitionInfo} onClose={closeModal}/>}
        </div>
    )
}

export default DealTypeFilter